import React, { useContext, useMemo } from "react";
import { DataTable, HighlightMatch, Table } from "@sscs/ui";
import { capitalizeFirstLetter } from "@sscs/utils";
import { ROOT_FILE_PATH } from "../../../constants";
import { expandedRowsUpdated, focusedRowUpdated, selectExpandedRow, selectFilter, selectFocusedRow, } from "../../../store/filesDiffSlice";
import { ExpandedRowDetails } from "../../../ui";
import { getFileName } from "../../../utils";
import { ReportContext } from "../../context";
import { useReportDispatch, useReportSelector } from "../../hooks";
import { useGetFileDiffDetailsQuery } from "../../services/diff.api";
import styles from "./diff.module.scss";
import ChangeSummaryCard from "./diffChanges/ChangeSummaryCard";
import EventChanges from "./diffChanges/EventChanges";
import IndicatorChanges from "./diffChanges/IndicatorChanges";
import PolicyChanges from "./diffChanges/PolicyChanges";
import TagChanges from "./diffChanges/TagChanges";
import ThreatHuntingChanges from "./diffChanges/ThreatHuntingChanges";
import UriChanges from "./diffChanges/UriChanges";
import VulnerabilityChanges from "./diffChanges/VulnerabilityChanges";
import { estimateDiffDetailsHeight, transformChanges } from "./diffUtils";
function isSingleFileDiff(file) {
    return file.changes !== undefined;
}
const singleSidedChange = (changeType, value) => {
    if (value === "" || (changeType !== "added" && changeType !== "removed"))
        return null;
    return {
        change: changeType,
        current: changeType === "added" ? value : "",
        previous: changeType === "removed" ? value : "",
        tags: [],
    };
};
const ChangeDetails = ({ file, changeType, expanded, }) => {
    const { diffId } = useContext(ReportContext);
    const { data: fetchedData } = useGetFileDiffDetailsQuery({
        ui_item_id: file.id,
        ui_id: diffId,
    }, { skip: !expanded || isSingleFileDiff(file) });
    const data = isSingleFileDiff(file) ? file : fetchedData;
    const { formatChange, sizeChange, tags, indicators, policies, vulnerabilities, classificationChange, functionalityChange, entropyChange, uris, actions, } = useMemo(() => transformChanges(data === null || data === void 0 ? void 0 : data.changes), [data]);
    const formatFact = formatChange !== null && formatChange !== void 0 ? formatChange : singleSidedChange(changeType, [file.file.type, file.file.subtype].filter(Boolean).join("/"));
    const sizeFact = sizeChange !== null && sizeChange !== void 0 ? sizeChange : singleSidedChange(changeType, String(file.file.size));
    if (!data)
        return (React.createElement(DataTable.ExpandedRow.Details, null,
            React.createElement(ExpandedRowDetails, null,
                React.createElement("div", { style: {
                        minHeight: `${estimateDiffDetailsHeight(file.changes_count, changeType)}px`,
                    } }))));
    const countByDirection = (changes) => ({
        added: changes.filter((change) => change.change === "added").length,
        removed: changes.filter((change) => change.change === "removed").length,
        changed: changes.filter((change) => change.change === "changed").length,
    });
    const narrativeCategories = [
        {
            singular: "threat hunting finding",
            plural: "threat hunting findings",
            added: data.violations.length,
            removed: 0,
            changed: 0,
        },
        { singular: "issue", plural: "issues", ...countByDirection(policies) },
        {
            singular: "vulnerability",
            plural: "vulnerabilities",
            ...countByDirection(vulnerabilities),
        },
        {
            singular: "network reference",
            plural: "network references",
            ...countByDirection(uris),
        },
        { singular: "tag", plural: "tags", ...countByDirection(tags) },
        { singular: "event", plural: "events", ...countByDirection(actions) },
        {
            singular: "behavior",
            plural: "behaviors",
            ...countByDirection(indicators),
        },
    ];
    return (React.createElement(DataTable.ExpandedRow.Details, null,
        React.createElement(ExpandedRowDetails, null,
            React.createElement(ChangeSummaryCard, { changeType: changeType, hashChange: file.hash_change, formatChange: formatFact, sizeChange: sizeFact, entropyChange: entropyChange, functionalityChange: functionalityChange, classificationChange: classificationChange, categories: narrativeCategories }),
            data.violations.length > 0 ? (React.createElement(ThreatHuntingChanges, { violations: data.violations })) : null,
            policies.length > 0 ? React.createElement(PolicyChanges, { diffPolicies: policies }) : null,
            vulnerabilities.length > 0 ? (React.createElement(VulnerabilityChanges, { vulnerabilitiesDiff: vulnerabilities })) : null,
            uris.length > 0 ? React.createElement(UriChanges, { uris: uris }) : null,
            tags.length > 0 ? React.createElement(TagChanges, { tags: tags }) : null,
            actions.length > 0 ? (React.createElement(EventChanges, { actions: actions, actionMappings: data.action_mappings })) : null,
            indicators.length > 0 ? (React.createElement(IndicatorChanges, { indicators: indicators })) : null)));
};
const FileChange = ({ rowIndex, file }) => {
    var _a, _b;
    const dispatch = useReportDispatch();
    const focusedRow = useReportSelector(selectFocusedRow);
    const expanded = useReportSelector((state) => selectExpandedRow(state, file.id));
    const { file_name: searchRegex } = useReportSelector(selectFilter);
    const totalCount = Object.values(file.changes_count).reduce((acc, count) => acc + count, 0);
    const filePath = file.file.path.split("/").slice(0, -1).join("/");
    const fileName = getFileName(file.file);
    const movedTo = (_a = file.name_change) === null || _a === void 0 ? void 0 : _a.current.split("/").slice(0, -1).join("/");
    const renamedTo = (_b = file.name_change) === null || _b === void 0 ? void 0 : _b.current.split("/").pop();
    return (React.createElement(DataTable.ExpandedRow, { id: `diff-modified-expand-row-${rowIndex}`, expanded: expanded, onChange: (isExpanded) => dispatch(expandedRowsUpdated({
            id: file.id,
            expand: isExpanded,
        })), focused: focusedRow === file.id, onClick: () => dispatch(focusedRowUpdated(file.id)) },
        React.createElement(DataTable.ExpandedRow.Summary, null,
            React.createElement(Table.Cell, null, capitalizeFirstLetter(file.change_type)),
            React.createElement(Table.Cell, null,
                filePath ? `${filePath}/` : ROOT_FILE_PATH,
                React.createElement("div", null, movedTo && movedTo !== filePath ? (React.createElement("div", { className: styles["renamed-to"] },
                    "Moved to: ",
                    movedTo,
                    "/")) : null)),
            React.createElement(Table.Cell, null,
                React.createElement(HighlightMatch, { value: fileName, regex: searchRegex }),
                renamedTo && renamedTo !== fileName ? (React.createElement("div", { className: styles["renamed-to"] },
                    "Renamed to: ",
                    renamedTo)) : null),
            React.createElement(Table.Cell, { align: "right", className: styles.count }, totalCount.toLocaleString())),
        React.createElement(ChangeDetails, { file: file, changeType: file.change_type, expanded: expanded })));
};
export default FileChange;
//# sourceMappingURL=FileChange.js.map